"use client";

import {
  Bar,
  CartesianGrid,
  BarChart as RechartsBarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from "recharts";
import { ChartContainer, type ChartConfig } from "./ChartContainer";
import { ChartTooltipContent } from "./ChartTooltipContent";

export type BarChartData = Record<string, string | number>;

export type BarChartProps = {
  /** Chart data (array of objects, e.g. [{ month: "January", desktop: 186 }]) */
  data: BarChartData[];
  /** Chart configuration */
  config: ChartConfig;
  /** Data key for X axis (e.g. "month") */
  xDataKey: string;
  /** Data key(s) for bar value(s) (e.g. "desktop" or ["desktop", "mobile"]) */
  valueDataKey: string | string[];
  /** X axis tick formatter (e.g. show first 3 chars) */
  xAxisTickFormatter?: (value: string) => string;
  /** Margin around chart */
  margin?: { top?: number; right?: number; bottom?: number; left?: number };
  /** Bar corner radius (default: 8) */
  barRadius?: number;
  /** Stack bars (default: false) */
  stacked?: boolean;
  /** Show vertical grid lines (default: false) */
  verticalGrid?: boolean;
  /** Show tooltip (default: true) */
  showTooltip?: boolean;
  /** Chart container className */
  className?: string;
};

export function BarChart({
  data,
  config,
  xDataKey,
  valueDataKey,
  xAxisTickFormatter,
  margin = { left: 12, right: 12 },
  barRadius = 8,
  stacked = false,
  verticalGrid = false,
  showTooltip = true,
  className,
}: BarChartProps) {
  const valueKeys = Array.isArray(valueDataKey) ? valueDataKey : [valueDataKey];

  return (
    <ChartContainer config={config} className={className}>
      <ResponsiveContainer width="100%" height="100%">
        <RechartsBarChart data={data} margin={margin} accessibilityLayer>
          <CartesianGrid vertical={verticalGrid} strokeDasharray="3 3" />
          <XAxis
            dataKey={xDataKey}
            tickLine={false}
            axisLine={false}
            tickMargin={10}
            tickFormatter={xAxisTickFormatter ?? ((v) => String(v))}
          />
          {showTooltip && (
            <Tooltip
              cursor={false}
              content={<ChartTooltipContent config={config} indicator="line" />}
            />
          )}
          {valueKeys.map((key, index) => {
            const color =
              config[key]?.color ??
              `var(--chart-${((index % 5) + 1) as 1 | 2 | 3 | 4 | 5})`;
            return (
              <Bar
                key={key}
                dataKey={key}
                fill={color}
                radius={barRadius}
                stackId={stacked ? "stack" : undefined}
              />
            );
          })}
        </RechartsBarChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
}
